/**
 * Shared merchant-stall chrome (HEX-106, ADR-0020 §4) — the lantern-lit "place,
 * not a dialog" vocabulary used by both {@link ShopFlow} and {@link MerchantFlow}.
 *
 * The town shop and the roaming traveller-merchant render the same stall: a
 * signboard header + gold pouch, a hooded-merchant niche with a greeting, a
 * counter of wares (display-mode Cards with price plates), and a footer shelf
 * for the map curiosity + actions. The two flows differ only in their copy,
 * their `data-role`/`data-action` identifiers, and what sits on the shelf — so
 * the structure lives here once and each flow assembles its own shelf contents.
 *
 * Presentation only (ADR-0020): the builders own DOM + theme-token styling; the
 * flows keep all economy / persistence behaviour. Wares are drawn through the
 * unified `renderCardFace` (HEX-103) — there is no second card face.
 */

import { footprint } from '../combat/grid';
import type { Card } from '../combat/types';
import { buildFootprintBadge, renderCardFace } from '../combat/ui/cardFace';
import { createGlyphElement } from '../render/glyphs';

/** A plain text label span for an action button (sits beside its glyph). */
export function labelSpan(text: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.textContent = text;
  return span;
}

/** A gold-tinted price token for an action button (e.g. the restock cost). */
export function goldSpan(text: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'hex6-stall__action-gold';
  span.textContent = text;
  return span;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function svgEl(tag: string, attrs: Record<string, string>): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

/**
 * The schematic hooded-merchant silhouette (ported from the handoff `shop.jsx`
 * `Merchant`): a cloak + hood mass in mossy darks with two ember-glow eyes.
 * Shapes are theme-token-coloured via CSS classes (presentation attributes
 * can't take `var()`), so the figure stays in the Moss & Bone palette.
 */
export function buildMerchantSilhouette(): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: '0 0 220 300',
    width: '200',
    height: '272',
    class: 'hex6-merchant',
    'aria-hidden': 'true',
  }) as SVGSVGElement;

  svg.appendChild(
    svgEl('ellipse', { cx: '110', cy: '150', rx: '95', ry: '140', class: 'hex6-merchant__shadow' }),
  );
  svg.appendChild(
    svgEl('path', {
      d: 'M58 300 C54 200 70 150 110 120 C150 150 166 200 162 300 Z',
      class: 'hex6-merchant__cloak',
    }),
  );
  svg.appendChild(
    svgEl('path', {
      d: 'M110 120 C92 132 86 150 90 175 L130 175 C134 150 128 132 110 120 Z',
      class: 'hex6-merchant__cloak-inner',
    }),
  );
  svg.appendChild(
    svgEl('ellipse', { cx: '110', cy: '104', rx: '34', ry: '38', class: 'hex6-merchant__hood' }),
  );
  svg.appendChild(
    svgEl('path', {
      d: 'M80 104 C80 70 140 70 140 104 C140 88 80 88 80 104 Z',
      class: 'hex6-merchant__hood-shade',
    }),
  );
  svg.appendChild(svgEl('circle', { cx: '100', cy: '106', r: '3', class: 'hex6-merchant__eye' }));
  svg.appendChild(svgEl('circle', { cx: '120', cy: '106', r: '3', class: 'hex6-merchant__eye' }));
  return svg;
}

export interface StallShell {
  /** The full-viewport scrim root — the flow appends this to its container. */
  readonly root: HTMLDivElement;
  /** The number-only gold readout (the flow refreshes its `textContent`). */
  readonly goldValue: HTMLSpanElement;
  /** The wares counter host — the flow fills it with {@link buildWareTile}s. */
  readonly wares: HTMLDivElement;
  /** Footer shelf — the flow prepends its map item / note before the actions. */
  readonly shelf: HTMLDivElement;
  /** Action-button row, already appended to the shelf (right-aligned). */
  readonly actions: HTMLDivElement;
}

export interface StallShellOptions {
  /** `data-role` on the scrim root (`shop-modal` / `merchant-modal`). */
  readonly modalRole: string;
  readonly ariaLabel: string;
  /** Signboard name (the stall's title). */
  readonly signTitle: string;
  /** Signboard kicker line, e.g. `TOWN SHOP · PERMANENT`. */
  readonly signSubtitle: string;
  /** `data-role` on the number-only gold value. */
  readonly goldRole: string;
  /** Hooded-merchant greeting line. */
  readonly greeting: string;
  /** `data-role` on the wares counter host (`shop-body` / `merchant-body`). */
  readonly waresRole: string;
}

/**
 * Build the stall shell: scrim → panel → signboard + pouch, merchant niche +
 * greeting, wares counter, footer shelf. Returns the live hooks the flow drives.
 */
export function buildStallShell(opts: StallShellOptions): StallShell {
  const root = document.createElement('div');
  root.className = 'hex6-interaction-modal hex6-stall-scrim';
  root.dataset.role = opts.modalRole;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-label', opts.ariaLabel);

  const stall = document.createElement('div');
  stall.className = 'hex6-stall';

  // ── signboard header ─────────────────────────────────────────────────────
  const sign = document.createElement('header');
  sign.className = 'hex6-stall__sign';

  const signLeft = document.createElement('div');
  signLeft.className = 'hex6-stall__sign-left';

  const mark = document.createElement('div');
  mark.className = 'hex6-stall__sign-mark';
  mark.appendChild(createGlyphElement('shop', { size: 26, colour: 'var(--gold)' }));

  const signText = document.createElement('div');
  const name = document.createElement('div');
  name.className = 'hex6-stall__name';
  name.textContent = opts.signTitle;
  const sub = document.createElement('div');
  sub.className = 'hex6-stall__sub';
  sub.textContent = opts.signSubtitle;
  signText.append(name, sub);
  signLeft.append(mark, signText);

  const pouch = document.createElement('div');
  pouch.className = 'hex6-stall__pouch';
  const coin = document.createElement('span');
  coin.className = 'hex6-stall__coin hex6-stall__coin--lg';
  const pouchText = document.createElement('div');
  const pouchLabel = document.createElement('div');
  pouchLabel.className = 'hex6-stall__pouch-label';
  pouchLabel.textContent = 'GOLD';
  const goldValue = document.createElement('span');
  goldValue.className = 'hex6-stall__pouch-value';
  goldValue.dataset.role = opts.goldRole;
  pouchText.append(pouchLabel, goldValue);
  pouch.append(coin, pouchText);

  sign.append(signLeft, pouch);

  // ── body: merchant niche + wares counter ─────────────────────────────────
  const body = document.createElement('div');
  body.className = 'hex6-stall__body';

  const niche = document.createElement('div');
  niche.className = 'hex6-stall__niche';
  niche.appendChild(buildMerchantSilhouette());
  const greeting = document.createElement('div');
  greeting.className = 'hex6-stall__greeting';
  greeting.textContent = opts.greeting;
  niche.appendChild(greeting);

  const counter = document.createElement('div');
  counter.className = 'hex6-stall__counter';
  const kicker = document.createElement('div');
  kicker.className = 'hex6-stall__kicker';
  kicker.textContent = 'On the counter';
  const wares = document.createElement('div');
  wares.className = 'hex6-stall__wares';
  wares.dataset.role = opts.waresRole;
  const slab = document.createElement('div');
  slab.className = 'hex6-stall__slab';
  slab.setAttribute('aria-hidden', 'true');
  counter.append(kicker, wares, slab);

  body.append(niche, counter);

  // ── footer shelf (map curiosity / note + actions) ────────────────────────
  const shelf = document.createElement('div');
  shelf.className = 'hex6-stall__shelf';
  const actions = document.createElement('div');
  actions.className = 'hex6-stall__actions';
  shelf.appendChild(actions);

  stall.append(sign, body, shelf);
  root.appendChild(stall);

  return { root, goldValue, wares, shelf, actions };
}

export interface WareTileOptions {
  readonly card: Card;
  readonly price: number;
  /** `data-role` on the ware row (`shop-card-offer` / `merchant-card-offer`). */
  readonly offerRole: string;
  /** `data-role` on the price plate (`shop-card-price` / `merchant-card-price`). */
  readonly priceRole: string;
  /** `data-action` on the buy button (`shop-buy-card` / `merchant-buy-card`). */
  readonly buyAction: string;
  readonly affordable: boolean;
  readonly fits: boolean;
  readonly onBuy: () => void;
}

/**
 * A single counter ware: a display-mode Card, a "takes N×M in your loadout"
 * footprint note (the real measured footprint), and a price plate with a Buy
 * button that disables when unaffordable or the inventory has no room.
 */
export function buildWareTile(o: WareTileOptions): HTMLDivElement {
  const tile = document.createElement('div');
  tile.className = 'hex6-stall__ware';
  tile.dataset.role = o.offerRole;
  tile.dataset.cardId = o.card.id;

  const cardHost = document.createElement('div');
  cardHost.className = 'hex6-stall__ware-card';
  cardHost.appendChild(renderCardFace(o.card, { mode: 'display' }));

  const fp = footprint(o.card, false);
  const foot = document.createElement('div');
  foot.className = 'hex6-stall__ware-foot';
  foot.appendChild(buildFootprintBadge(o.card, false));
  const footText = document.createElement('span');
  footText.textContent = `takes ${fp.cols}×${fp.rows} in your loadout`;
  foot.appendChild(footText);

  const plate = document.createElement('div');
  plate.className = 'hex6-stall__plate';
  const priceChip = document.createElement('span');
  priceChip.className = 'hex6-stall__price';
  const priceCoin = document.createElement('span');
  priceCoin.className = 'hex6-stall__coin';
  const priceValue = document.createElement('span');
  priceValue.dataset.role = o.priceRole;
  priceValue.textContent = `${o.price}g`;
  priceChip.append(priceCoin, priceValue);

  const buy = document.createElement('button');
  buy.type = 'button';
  buy.dataset.action = o.buyAction;
  buy.className = 'btn primary hex6-stall__buy';
  buy.textContent = 'Buy';
  buy.disabled = !o.affordable || !o.fits;
  buy.title = !o.affordable ? 'Not enough gold' : !o.fits ? 'No inventory room' : '';
  buy.addEventListener('click', o.onBuy);

  plate.append(priceChip, buy);
  tile.append(cardHost, foot, plate);
  return tile;
}
